"use client";

import React, { useMemo } from "react";
import type { Post } from "@/payload-types";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import { formatRelativeTime } from "@/utilities/formatting/date";

interface CategoryArticlesSidebarProps {
    /** The initial article that was opened (stays at top of sidebar) */
    initialPost: Post;
    /** Other posts from the same category */
    categoryPosts: Post[];
    /** Currently active article ID (changes as user scrolls) */
    activePostId: number;
}

export function CategoryArticlesSidebar({ initialPost, categoryPosts, activePostId }: CategoryArticlesSidebarProps) {
    const nav = useNavigation();
    // Build stable ordered list: initial article first, then category posts (excluding initial)
    // This list doesn't change when user scrolls - only the active indicator changes
    const orderedPosts = useMemo(() => {
        const seen = new Set<number>();
        const result: Post[] = [];

        // First, add the initial post (the one user navigated to)
        result.push(initialPost);
        seen.add(initialPost.id);

        // Then add category posts, skipping duplicates
        for (const post of categoryPosts) {
            if (!seen.has(post.id)) {
                result.push(post);
                seen.add(post.id);
            }
        }

        return result;
    }, [initialPost, categoryPosts]); // Only depends on initial data, not activePostId

    // Get source/author info
    const getSource = (post: Post): string => {
        if (post.populatedAuthors && post.populatedAuthors.length > 0) {
            return post.populatedAuthors[0].name || "Staff";
        }
        return "Staff";
    };

    return (
        <aside className="hidden md:block w-56 lg:w-72 shrink-0 h-full overflow-y-auto border-r border-border bg-background">
            <div className="py-4">
                {orderedPosts.map((post) => {
                    const isActive = post.id === activePostId;
                    const url = nav.post(post);

                    return (
                        <a key={post.id} href={url} className="block group">
                            <div
                                className={`relative px-4 py-3 transition-colors ${
                                    isActive ? "bg-muted/50" : "hover:bg-muted/30"
                                }`}
                            >
                                {/* Active indicator border */}
                                {isActive && <div className="absolute left-0 top-0 bottom-0 w-1 bg-amber-500" />}
                                {/* Regular border for non-active articles */}
                                {!isActive && (
                                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-transparent group-hover:bg-muted" />
                                )}

                                <h3 className="text-sm font-medium leading-snug line-clamp-3 group-hover:text-primary transition-colors">
                                    {post.title}
                                </h3>
                                <div className="mt-1.5 flex items-center gap-1.5 text-xs text-foreground/50">
                                    <span>{formatRelativeTime(post.publishedAt)}</span>
                                    <span>·</span>
                                    <span>{getSource(post)}</span>
                                </div>
                            </div>
                        </a>
                    );
                })}

                {/* End message */}
                <div className="px-4 py-6 text-center">
                    <p className="text-sm text-foreground/40">You&apos;re all caught up!</p>
                </div>
            </div>
        </aside>
    );
}
